import React from 'react';
import type { ComponentProps } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import {
  alignItems,
  backgroundColor,
  borderRadius,
  color,
  fontWeight,
  justifyContent,
  size,
  textAlign,
  themeGet,
} from 'styled-system';

import { font } from '../../theme/helper';
import { themeWidth } from '../../theme/system-utilities';
import { ButtonBase, SPINNER_SIZE } from './Base';

const UnstyledButtonPrimary = styled(ButtonBase)<any>`
  ${color};
  ${fontWeight};
  ${textAlign};
  ${alignItems};
  ${justifyContent};
  ${borderRadius};
  ${backgroundColor};
  ${themeWidth};
  ${font(3)};

  border-color: ${themeGet('colors.sys.primary.background.default')};

  cursor: ${({ isLoading, isDisabled }) =>
    isLoading ? 'progress' : isDisabled ? 'not-allowed' : 'pointer'};

  ${props =>
    props.isLoading &&
    css`
      background-color: ${themeGet('colors.sys.primary.background.default')(props)};
    `};

  &:not(:focus) {
    outline: none;
  }

  [role='progressbar'] svg {
    ${({ spinnerSize }) => size(spinnerSize || SPINNER_SIZE)};
    stroke: ${themeGet('colors.sys.neutral.background.default')};
  }

  ${props =>
    !props.isDisabled
      ? css`
          &:hover,
          &:focus {
            color: ${themeGet('colors.sys.neutral.background.default')(props)};
            background-color: ${themeGet('colors.sys.primary.background.hover')(props)};
            border-color: ${themeGet('colors.sys.primary.background.hover')(props)};
          }
          &:active {
            color: ${themeGet('colors.sys.neutral.background.default')(props)};
            background-color: ${themeGet('colors.sys.primary.background.active')(props)};
            border-color: ${themeGet('colors.sys.primary.background.active')(props)};
          }
        `
      : css`
          color: ${themeGet('colors.sys.neutral.background.default')(props)};
          background-color: ${themeGet(
            props.isLoading
              ? 'colors.sys.primary.background.default'
              : 'colors.sys.neutral.background.inverted'
          )(props)};
          border-color: ${themeGet(
            props.isLoading
              ? 'colors.sys.primary.background.default'
              : 'colors.sys.neutral.background.inverted'
          )(props)};
        `};
`;

export const ButtonPrimary = (props: ComponentProps<typeof UnstyledButtonPrimary>) => (
  <UnstyledButtonPrimary
    px="sp_16"
    py="sp_8"
    color="sys.neutral.background.default"
    fontWeight="bold"
    textAlign="center"
    alignItems="center"
    justifyContent="center"
    borderRadius="small"
    backgroundColor="sys.primary.background.default"
    borderStyle="solid"
    borderWidth="small"
    borderColor="sys.primary.background.default"
    {...props}
  />
);
